// src/components/workflows/nodes/WebhookReceiverNode.jsx - Webhook Receiver Node (Display Only)
import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Badge } from '../../ui/badge.tsx';
import { Inbox, Clock } from 'lucide-react';

/**
 * WebhookReceiverNode - Displays callback/result receiver (display only)
 *
 * Node data structure:
 * {
 *   type: 'webhookReceiver',
 *   data: {
 *     label: 'Receive Results',
 *     description: 'Collects logs and data from automation',
 *     timeout_minutes: 30,
 *     expectedFields: ['status', 'result_summary', 'result_data', 'logs']
 *   }
 * }
 */
const WebhookReceiverNode = ({ data, selected, id }) => {
  const {
    label = 'Receive Results',
    description,
    timeout_minutes = 30,
    expectedFields = []
  } = data;

  return (
    <>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-cyan-500"
      />

      <div className={`
        w-56 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-cyan-500 shadow-lg border-cyan-500/50' : 'shadow-md border-cyan-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-cyan-500/10 to-cyan-600/5
      `}>
        {/* Header */}
        <div className="p-3 pb-2 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-cyan-400" />
              <div className="text-base font-semibold text-cyan-100">
                {label}
              </div>
            </div>
            <Badge variant="outline" className="text-xs border-cyan-500/50 text-cyan-300">
              Callback
            </Badge>
          </div>
          {description && (
            <div className="text-xs text-slate-400 mt-1">
              {description}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3 pt-2 space-y-2">
          {/* Timeout info */}
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5" />
            <span>Timeout: {timeout_minutes} minutes</span>
          </div>

          {/* Expected fields preview */}
          {expectedFields.length > 0 && (
            <div className="text-xs">
              <div className="text-slate-500 mb-1">Receives:</div>
              <div className="flex flex-wrap gap-1">
                {expectedFields.slice(0, 3).map((field, index) => (
                  <span
                    key={index}
                    className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-400 border border-slate-700"
                  >
                    {field}
                  </span>
                ))}
                {expectedFields.length > 3 && (
                  <span className="px-1.5 py-0.5 text-slate-500">
                    +{expectedFields.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* No output handle - this is the end (or could add one if chaining) */}
    </>
  );
};

export default WebhookReceiverNode;
